/**
 * `task.v1` — wire → canonical decoder for Task-shaped cells.
 *
 * The wire shape (`SheetsNetwork.Task`) is a superset of the canonical
 * `dtype.TaskCellValue`. The decoder validates required fields
 * (`id` + `title`), narrows `status` to the canonical union, and
 * passes `DateTime<Utc>` fields through unchanged — they're
 * already hydrated at the SheetsNetwork layer.
 *
 * Null return: missing `id` or `title` drops the cell.
 *
 * Tolerant (`WireDecode.partial`), not strict: a task cell may be an older
 * stored shape or the SDK's own optimistic snapshot, and one undecodable
 * field must not drop the task.
 */

import { TaskId } from "../ids/ids.ts";
import type { CellValue, TaskCellValue } from "../core/dtype.ts";
import type { EncodingDecoder } from "../core/encoding-registry.ts";
import { Json, jsonObjectOf } from "../core/json.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";
import { WireDecode } from "../wire/wire-decode.ts";
import type { Option } from "../core/option.ts";

/** The `task.v1` wire-encoding name a task source row declares in its metadata. */
export const TASK_V1 = "task.v1";

const TASK_STATUSES: readonly TaskCellValue.Status[] = [
  "open",
  "in-progress",
  "done",
  "cancelled",
];

/** Decodes a `task.v1` wire cell to a canonical task (`null`: not a task). */
export const taskV1: EncodingDecoder = (wire): Option<Json | CellValue> => {
  // The JSON object the wire sent, its declared fields decoded (dates
  // hydrated; a field that does not decode is left out).
  const json = jsonObjectOf(wire);
  if (json == null) return null;

  const parsed = WireDecode.partial(SheetsNetwork.TaskCell.MODEL, json).value;
  const id = typeof parsed.id === "string" ? TaskId.parse(parsed.id) : null;
  if (id == null) return null;
  if (typeof parsed.title !== "string" || parsed.title === "") return null;

  // The canonical status union is not the wire's (`TaskStatus`): keep the
  // raw string only when it is a canonical value.
  const status = Json.oneOf(json, "status", TASK_STATUSES);

  return {
    id,
    title: parsed.title,
    status,
    // Display fields surfaced by the Tasks panel's ID + Priority (`importance`)
    // columns; a field the wire left out (or that did not decode) is `null`.
    importance: parsed.importance ?? null,
    displayId: parsed.displayId ?? null,
    displayNumber: parsed.displayNumber ?? null,
    scheduledStart: parsed.scheduledStart ?? null,
    scheduledEnd: parsed.scheduledEnd ?? null,
    dueDate: parsed.dueDate ?? null,
    completedAt: parsed.completedAt ?? null,
  } satisfies TaskCellValue;
};
